import React, { useState, useEffect, useRef, useCallback } from 'react';
import CrowdInsight from '../../components/events/CrowdInsight';
import { useNavigate } from 'react-router-dom';
import { MainLayout } from '../../components/layout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import Badge from '../../components/ui/Badge';
import Loading from '../../components/ui/Loading';
import Alert from '../../components/ui/Alert';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { Search, Eye, Check, X, Filter, CheckCircle, CheckSquare, XCircle, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { eventService } from '../../services';
import { useLocation } from '../../contexts/LocationContext';
import { EventThumbnail } from '../../components/events/EventThumbnail';
import { EventMediaModal } from '../../components/events/EventMediaModal';
import RegisteredPeopleLinks from '../../components/events/RegisteredPeopleLinks';
import type { Event } from '../../types';
import { formatEventDate } from '../../utils/eventTime';
import type { EventSummary } from '../../services/event.service';

const EventsPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLocationId, locations } = useLocation();
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterState, setFilterState] = useState('all');
  const filterLocation = selectedLocationId;
  const [filterTag, setFilterTag] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMedia, setSelectedMedia] = useState<{ event: Event; type: 'video' | 'image' } | null>(null);
  const [processingAction, setProcessingAction] = useState<{ id: number; action: 'ack' | 'proc' } | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageSelection, setPageSelection] = useState({ key: '', page: 1 });
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [summary, setSummary] = useState<EventSummary | null>(null);
  const [textFilters, setTextFilters] = useState({ search: '', tag: '' });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const eventRequestRef = useRef(0);
  const eventAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setTextFilters({ search: searchTerm.trim(), tag: filterTag.trim() }), 350);
    return () => clearTimeout(timer);
  }, [searchTerm, filterTag]);

  // A changed filter starts at page one immediately, including header locations.
  const pageKey = JSON.stringify([filterType, filterState, filterLocation, textFilters, startDate, endDate, itemsPerPage]);
  const currentPage = pageSelection.key === pageKey ? pageSelection.page : 1;
  const setCurrentPage = (page: number) => setPageSelection({ key: pageKey, page });

  /**
   * Build API filter string from UI filter state
   * Format: field::value,field::value
   */
  const buildApiFilters = (): string => {
    const filters: string[] = [];
    
    // Event type filter
    if (filterType && filterType !== 'all') {
      filters.push(`event_type::${filterType}`);
    }
    
    // Event state filter
    if (filterState && filterState !== 'all') {
      filters.push(`event_state::${filterState}`);
    }
    
    // Location filter
    if (filterLocation && filterLocation !== 'all') {
      filters.push(`event_location::${filterLocation}`);
    }
    
    // Event tag filter (partial match supported by API)
    if (textFilters.tag) {
      filters.push(`event_tag::${textFilters.tag}`);
    }
    
    // Date range filters
    if (startDate) {
      filters.push(`created_date_gte::${startDate}`);
    }
    if (endDate) {
      filters.push(`created_date_lte::${endDate}`);
    }
    
    return filters.join(',');
  };

  const apiFilters = buildApiFilters();
  const fetchEvents = useCallback(async (showLoading = true) => {
    // Background polling must not supersede a request still in progress.
    if (!showLoading && (eventAbortRef.current || document.hidden)) return;
    eventAbortRef.current?.abort();
    const controller = new AbortController();
    eventAbortRef.current = controller;
    const request = ++eventRequestRef.current;
    let timedOut = false;
    const timeout = setTimeout(() => {
      timedOut = true;
      controller.abort();
      if (request === eventRequestRef.current) {
        setIsLoading(false);
        setError('Events are taking too long to load. Retrying shortly.');
      }
    }, 30000);
    try {
      if (showLoading) setIsLoading(true);
      setError(null);
      const response = await eventService.getEvents({
        sort: 'created_date,desc',
        page: currentPage,
        size: itemsPerPage,
        filter: apiFilters || undefined,
        search: textFilters.search || undefined,
      }, controller.signal);

      if (request === eventRequestRef.current && !controller.signal.aborted) {
        setEvents(response.data || []);
        setTotalElements(response.totalElements ?? response.data?.length ?? 0);
        setTotalPages(response.totalPages ?? Math.ceil((response.data?.length ?? 0) / itemsPerPage));
        setSummary(response.summary || null);
      }
    } catch (err) {
      if (request === eventRequestRef.current && (!controller.signal.aborted || timedOut)) {
        setError(timedOut ? 'Events are taking too long to load. Retrying shortly.' :
          err instanceof Error ? err.message : 'Failed to fetch events');
      }
    } finally {
      clearTimeout(timeout);
      if (eventAbortRef.current === controller) eventAbortRef.current = null;
      if (request === eventRequestRef.current) setIsLoading(false);
    }
  }, [apiFilters, currentPage, itemsPerPage, textFilters.search]);

  useEffect(() => {
    let active = true;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async (showLoading: boolean) => {
      await fetchEvents(showLoading);
      if (active) timer = setTimeout(() => { void refresh(false); }, 5000);
    };
    void refresh(true);
    return () => {
      active = false;
      clearTimeout(timer);
      eventRequestRef.current += 1;
      eventAbortRef.current?.abort();
      eventAbortRef.current = null;
    };
  }, [fetchEvents]);

  const handleAcknowledge = async (eventId: number) => {
    if (!window.confirm('Are you sure you want to acknowledge this event?')) {
      return;
    }
    
    setProcessingAction({ id: eventId, action: 'ack' });
    try {
      const updatedEvent = await eventService.acknowledgeEvent(eventId);
      
      // Update the event in local state with response from API
      setEvents(prevEvents => 
        prevEvents.map(event => 
          event.event_id === eventId 
            ? { ...event, ...updatedEvent }
            : event
        )
      );
      
      setSuccessMessage('Event acknowledged successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge event');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleProcess = async (eventId: number) => {
    setProcessingAction({ id: eventId, action: 'proc' });
    try {
      await eventService.processEvent(eventId);
      await fetchEvents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process event');
    } finally {
      setProcessingAction(null);
    }
  };

  const handleThumbnailClick = (event: Event) => {
    if (event.event_video_url) {
      setSelectedMedia({ event, type: 'video' });
    } else if (event.s3_url || event.event_snapshot_url) {
      setSelectedMedia({ event, type: 'image' });
    }
  };

  const handleTypeFilter = (type: string) => {
    setFilterType(type);
    setCurrentPage(1);
    // Filters trigger useEffect to fetch from API
  };

  const handleStateFilter = (state: string) => {
    setFilterState(state);
    setCurrentPage(1);
    // Filters trigger useEffect to fetch from API
  };

  // Filtering and pagination happen in the database; only this page is loaded.
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + events.length;
  const pageNumbers = Array.from(new Set([1, currentPage - 1, currentPage, currentPage + 1, totalPages]))
    .filter(page => page >= 1 && page <= totalPages).sort((a, b) => a - b);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const formatDate = (event: Event) => formatEventDate(event.created_date, {
    timeZone: event.location_timezone || locations.find(location => location.ycl_id === event.event_location)?.location_timezone,
    naiveIsLocal: event.event_source === 'cloud_alarm',
  });

  const getStateVariant = (state?: string): 'success' | 'warning' | 'danger' | 'info' => {
    switch (state?.toLowerCase()) {
      case 'active': return 'warning';
      case 'resolved': return 'success';
      case 'dismissed': return 'info';
      default: return 'info';
    }
  };

  const columns = [
    {
      key: 'event_id',
      label: 'ID',
      render: (event: Event) => (
        <div className="text-sm font-medium text-text-primary">#{event.event_id}</div>
      ),
    },
    {
      key: 'thumbnail',
      label: 'Thumbnail',
      render: (event: Event) => (
        <EventThumbnail
          event={event}
          onClick={() => handleThumbnailClick(event)}
        />
      ),
    },
    {
      key: 'event_type',
      label: 'Type',
      render: (event: Event) => (
        <div className="text-sm text-text-primary">
          {event.event_type ? event.event_type.replace(/_/g, ' ') : 'N/A'}
          <CrowdInsight metadata={event.ai_metadata} compact />
        </div>
      ),
    },
    {
      key: 'event_tag',
      label: 'Tag',
      render: (event: Event) => (
        <div className="text-sm text-text-secondary">
          {event.event_tag || 'N/A'}
        </div>
      ),
    },
    {
      key: 'ai_description',
      label: 'AI Analysis',
      render: (event: Event) => (
        <div className="max-w-xs">
          {event.ai_status === 'completed' && event.ai_description ? (
            <div>
              <div className="flex items-center gap-1 mb-1">
                <Sparkles size={12} className="text-purple-500" />
                <span className="text-xs text-purple-500 font-medium">AI</span>
              </div>
              <p className="text-xs text-text-primary line-clamp-2" title={event.ai_description}>
                {event.ai_description}
              </p>
            </div>
          ) : event.ai_status === 'processing' ? (
            <div className="flex items-center gap-1 text-xs text-text-muted">
              <Sparkles size={12} className="animate-pulse" />
              <span>Processing...</span>
            </div>
          ) : (
            <span className="text-xs text-text-muted">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'event_camera',
      label: 'Camera',
      render: (event: Event) => (
        <div>
          <div className="text-sm text-text-primary">
            {event.camera_name || event.event_camera || 'N/A'}
          </div>
          <div className="text-xs text-text-muted">
            {event.location_name || (event.event_location ? `Location #${event.event_location}` : '')}
          </div>
        </div>
      ),
    },
    {
      key: 'event_state',
      label: 'State',
      render: (event: Event) => (
        <Badge variant={getStateVariant(event.event_state)} size="sm">
          {event.event_state || 'N/A'}
        </Badge>
      ),
    },
    {
      key: 'registered_persons',
      label: 'Person Registry',
      render: (event: Event) => event.registered_persons?.length
        ? <RegisteredPeopleLinks people={event.registered_persons} />
        : <span className="text-xs text-text-muted">—</span>,
    },
    {
      key: 'confidence',
      label: 'Confidence',
      render: (event: Event) => (
        <div className="text-sm text-text-secondary">
          {event.confidence != null ? `${(event.confidence * 100).toFixed(0)}%` : 'N/A'}
        </div>
      ),
    },
    {
      key: 'acknowledged',
      label: 'Status',
      render: (event: Event) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            {event.acknowledged ? (
              <Check size={14} className="text-status-success" />
            ) : (
              <X size={14} className="text-text-muted" />
            )}
            <span className="text-xs text-text-muted">Ack</span>
          </div>
          <div className="flex items-center gap-1">
            {event.processed ? (
              <Check size={14} className="text-status-success" />
            ) : (
              <X size={14} className="text-text-muted" />
            )}
            <span className="text-xs text-text-muted">Proc</span>
          </div>
        </div>
      ),
    },
    {
      key: 'created_date',
      label: 'Time',
      render: (event: Event) => (
        <div className="text-sm text-text-muted">{formatDate(event)}</div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (event: Event) => (
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleAcknowledge(event.event_id)}
            disabled={event.acknowledged || (processingAction?.id === event.event_id && processingAction?.action === 'ack')}
            title={event.acknowledged ? "Already Acknowledged" : "Acknowledge"}
          >
            <CheckCircle size={16} className={event.acknowledged ? "text-status-success" : "text-text-muted"} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Eye size={16} />}
            onClick={() => navigate(`/events/${event.event_id}/view`)}
          >
            View
          </Button>
        </div>
      ),
    },
  ];

  const eventTypeOptions = [
    { label: 'All Types', value: 'all' },
    { label: 'Motion Detection', value: 'Motion Detection' },
    { label: 'Line Crossing', value: 'Line Crossing' },
    { label: 'Intrusion', value: 'Intrusion' },
    { label: 'Crowd Detection', value: 'crowd_detection' },
    { label: 'Firearm Detection', value: 'firearm_detection' },
    { label: 'Face Detection', value: 'Face Detection' },
    { label: 'License Plate', value: 'License Plate' },
    { label: 'Tampering', value: 'Tampering' },
    { label: 'Video Loss', value: 'Video Loss' },
    { label: 'Other Events', value: 'Event' },
  ];

  const eventStateOptions = [
    { label: 'All States', value: 'all' },
    { label: 'Active', value: 'active' },
    { label: 'Resolved', value: 'resolved' },
    { label: 'Dismissed', value: 'dismissed' },
  ];

  const stats = [
    {
      label: 'Total Events',
      value: totalElements,
      variant: 'info' as const,
    },
    {
      label: 'Unacknowledged',
      value: summary?.unacknowledged ?? events.filter(e => !e.acknowledged).length,
      variant: 'warning' as const,
    },
    {
      label: 'Active',
      value: summary?.active ?? events.filter(e => e.event_state === 'active').length,
      variant: 'danger' as const,
    },
    {
      label: 'Resolved',
      value: summary?.resolved ?? events.filter(e => e.event_state === 'resolved').length,
      variant: 'success' as const,
    },
  ];

  return (
    <MainLayout>
      <div className="page-stack">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1 className="page-title">Events</h1>
            <p className="page-description">Monitor and manage security events</p>
          </div>
          <Button
            variant="primary"
            leftIcon={<Sparkles size={18} />}
            onClick={() => navigate('/ai-search')}
          >
            AI Search
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 lg:gap-4">
          {stats.map((stat) => (
            <Card key={stat.label} padding="md">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-text-muted break-words">{stat.label}</p>
                  <p className="text-2xl font-bold text-text-primary mt-1">{stat.value}</p>
                </div>
                <span className={`hidden sm:flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                  stat.variant === 'warning' ? 'bg-status-warning/10 text-status-warning' :
                  stat.variant === 'danger' ? 'bg-status-danger/10 text-status-danger' :
                  stat.variant === 'success' ? 'bg-status-success/10 text-status-success' : 'bg-accent-primary/10 text-accent-primary'
                }`}>
                  {stat.variant === 'success' ? <CheckCircle size={18} /> : stat.variant === 'danger' ? <XCircle size={18} /> : stat.variant === 'warning' ? <CheckSquare size={18} /> : <Filter size={18} />}
                </span>
              </div>
            </Card>
          ))}
        </div>

        {error && <Alert variant="error" title="Error" message={error} />}

        {successMessage && <Alert variant="success" title="Success" message={successMessage} />}

        {/* Filters */}
        <Card>
          <div className="flex items-center gap-2 mb-4">
            <Filter size={18} className="text-text-muted" />
            <h2 className="text-sm font-semibold text-text-primary">Filters</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
            <Input
              label="Search"
              placeholder="Search events..."
              maxLength={200}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              leftIcon={<Search size={18} />}
            />
            <Select
              label="Event Type"
              value={filterType}
              onChange={(e) => handleTypeFilter(e.target.value)}
              options={eventTypeOptions}
            />
            <Select
              label="State"
              value={filterState}
              onChange={(e) => handleStateFilter(e.target.value)}
              options={eventStateOptions}
            />
            <Input
              label="Event Tag"
              placeholder="Filter by tag..."
              value={filterTag}
              onChange={(e) => setFilterTag(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Start Date"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <Input
              label="End Date"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setSearchTerm('');
                  setFilterType('all');
                  setFilterState('all');
                  setFilterTag('');
                  setStartDate('');
                  setEndDate('');
                }}
                fullWidth
              >
                Clear All Filters
              </Button>
            </div>
          </div>
        </Card>

        {/* Events Table */}
        <Card padding="none">
          {isLoading ? (
            <div className="p-8">
              <Loading size="lg" text="Loading events..." />
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12 px-4">
              <p className="text-text-muted">No events found</p>
            </div>
          ) : (
            <>
              <Table
                columns={columns}
                data={events}
                isLoading={isLoading}
              />
              
              {/* Pagination Controls */}
              <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-border-default">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-xs text-text-muted">Show</span>
                  <Select
                    aria-label="Events per page"
                    value={itemsPerPage.toString()}
                    onChange={(e) => handleItemsPerPageChange(e.target.value)}
                    options={[
                      { label: '10', value: '10' },
                      { label: '25', value: '25' },
                      { label: '50', value: '50' },
                      { label: '100', value: '100' },
                    ]}
                    className="w-20"
                  />
                  <span className="text-xs text-text-muted">
                    Showing {startIndex + 1} to {Math.min(endIndex, totalElements)} of {totalElements} events
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    leftIcon={<ChevronLeft size={16} />}
                  >
                    Previous
                  </Button>
                  
                  <span className="sm:hidden text-xs text-text-muted">{currentPage} / {totalPages}</span>
                    <div className="hidden sm:flex items-center gap-1">
                    {pageNumbers.map((page, index, array) => {
                        // Add ellipsis if there's a gap
                        const showEllipsisBefore = index > 0 && page - array[index - 1] > 1;
                        return (
                          <React.Fragment key={page}>
                            {showEllipsisBefore && (
                              <span className="px-2 text-text-muted">...</span>
                            )}
                            <Button
                              variant={currentPage === page ? 'primary' : 'ghost'}
                              size="sm"
                              onClick={() => handlePageChange(page)}
                              className="min-w-[2.5rem]"
                            >
                              {page}
                            </Button>
                          </React.Fragment>
                        );
                      })}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    rightIcon={<ChevronRight size={16} />}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>

        {/* Media Modal */}
        <EventMediaModal
          event={selectedMedia?.event || null}
          mediaType={selectedMedia?.type || 'image'}
          onClose={() => setSelectedMedia(null)}
        />
      </div>
    </MainLayout>
  );
};

export default EventsPage;

